import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

/**
 * OAuth callback landing page.
 *
 * Emergent Auth redirects the browser to `/auth/callback#session_id=<sid>`.
 * We synchronously read the fragment, POST it to our backend to exchange for
 * our own JWT cookies, then bounce the user to the dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [error, setError] = useState("");
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            setError("No session token found. Please try signing in again.");
            return;
        }
        const sessionId = decodeURIComponent(match[1]);

        (async () => {
            try {
                const { data } = await api.post("/auth/google/session", { session_id: sessionId });
                // Clean the fragment so a refresh doesn't re-run this flow
                window.history.replaceState(null, "", window.location.pathname);
                if (data?.pending) {
                    nav("/access-pending", {
                        replace: true,
                        state: { email: data.email, name: data.name, picture: data.picture },
                    });
                    return;
                }
                // Approved — hydrate AuthContext and land on the dashboard
                await refresh?.();
                nav("/", { replace: true });
            } catch (err) {
                setError(formatApiError(err) || "Google sign-in failed.");
            }
        })();
    }, [nav, refresh]);

    return (
        <div className="min-h-screen grid place-items-center login-scope">
            <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-8 mx-4" data-testid="auth-callback">
                <div className="flex items-center gap-3 mb-4">
                    <div className="login-brandmark">
                        <ShieldCheck size={22} strokeWidth={2} />
                    </div>
                    <div className="leading-tight">
                        <div className="text-[15px] font-bold text-slate-900 tracking-[0.14em] leading-none">
                            CREDIT CARD
                        </div>
                        <div className="text-[13px] font-semibold text-slate-500 tracking-[0.14em] leading-none mt-1.5">
                            CONTROL CENTER
                        </div>
                    </div>
                </div>
                {!error ? (
                    <div className="flex items-center gap-3 text-slate-600 text-sm py-4">
                        <Loader2 size={18} className="animate-spin text-blue-600" />
                        Finalising your Google sign-in…
                    </div>
                ) : (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="auth-callback-error">
                        <div className="flex items-start gap-2.5">
                            <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" />
                            <div>
                                <div className="text-red-900 font-semibold text-sm">Sign-in failed</div>
                                <div className="text-red-800 text-sm mt-0.5">{error}</div>
                                <button
                                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    onClick={() => nav("/login", { replace: true })}
                                    data-testid="auth-callback-back-btn"
                                >
                                    Back to sign in
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
